// medicationService.js — Service de gestion des informations médicamenteuses
// ✅ CORRIGÉ : Sécurité pédiatrique pour utilisateurs non-medical

import { auth } from '../firebase';

const FUNCTIONS_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://europe-west1-medaviceia.cloudfunctions.net';

export const medicationService = {
  /**
   * Récupère les informations complètes d'un médicament
   * @param {string} medicationName - Nom du médicament
   * @param {string} userProfile - Profil utilisateur (medecin/infirmier/non_medical)
   * @returns {Promise<Object>} Informations structurées du médicament
   */
  async getMedicationInfo(medicationName, userProfile = 'non_medical') {
    const profileAdaptation = {
      medecin: `NIVEAU : Expert médical
- Inclus pharmacodynamie, pharmacocinétique détaillée
- Mentionne mécanisme d'action précis
- Ajoute ajustements posologiques (insuffisance rénale/hépatique, poids, âge)
- Cite les études pivots ou recommandations HAS/ANSM
- Inclus monitoring biologique et clinique nécessaire
- Précise la classification ATC et groupe thérapeutique`,

      infirmier: `NIVEAU : Professionnel IDE
- Focus surveillance clinique et paramètres vitaux
- Détaille modalités d'administration (reconstitution, dilution, débit)
- Précise éléments de traçabilité et transmissions
- Mentionne surveillance post-administration
- Inclus éducation thérapeutique du patient`,

      non_medical: `NIVEAU : Grand public
- Vulgarise les termes médicaux
- Utilise des analogies simples
- Focus conseils pratiques d'utilisation
- Signes d'alerte accessibles`,
    };

    const systemPrompt = `Tu es un expert pharmaceutique de niveau ${userProfile === 'medecin' ? 'universitaire' : userProfile === 'infirmier' ? 'hospitalier' : 'pédagogue'}.

${profileAdaptation[userProfile] || profileAdaptation.non_medical}

RÈGLES STRICTES :
- Réponds en français
- Format JSON structuré UNIQUEMENT
- Informations vérifiées et scientifiquement exactes
- Si le médicament n'existe pas ou n'est pas reconnu, retourne "exists": false
- Adapte le niveau de détail et vocabulaire au profil utilisateur

FORMAT JSON ATTENDU :
{
  "exists": true,
  "name": "Nom commercial",
  "dci": "Dénomination Commune Internationale",
  "classification": "Classe thérapeutique",

  "indications": "Texte structuré",
  "mecanismeAction": "Texte structuré",
  "posologie": "Texte structuré adapté au profil",

  "administration": "Modalités pratiques",
  "presentations": "Formes et dosages",
  "composition": "Principe actif + excipients notables",

  "contreIndications": [
    "Liste simple",
    "Liste simple"
  ],

  "effetsIndesirables": [
    "Effets fréquents",
    "Effets graves"
  ],

  "interactions": [
    "Interaction majeure",
    "Interaction alimentaire",
    "Interaction biologique"
  ],

  "grossesse": "Recommandations grossesse/allaitement",
  "surveillance": "Surveillance clinique et biologique",
  "conservation": "Conditions de conservation",
  "conduite": "Impact conduite",
  "surdosage": "Conduite à tenir",
  "educationPatient": "Conseils patient",
  "references": "Source ou recommandation officielle"
}

⚠️ IMPORTANT : 
- Retourne UNIQUEMENT le JSON valide
- Pas de texte avant/après, pas de balises markdown
- Si champ non applicable au profil, mettre chaîne vide
- Adapter exhaustivité au profil (médecin = max détails, non_medical = essentiel)`;

    const messages = [
      { role: 'system', content: systemPrompt },
      {
        role: 'user',
        content: `Fournis les informations pharmaceutiques complètes sur : ${medicationName}
        
Profil utilisateur : ${userProfile}
Adapte le niveau de détail en conséquence.`,
      },
    ];

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${FUNCTIONS_URL}/getMedicationInfo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ medicationName, userProfile }),
      });

      const rawText = await response.text();

      if (!response.ok) {
        const errorData = JSON.parse(rawText);

        if (response.status === 429 && errorData.error === 'quota_exceeded') {
          throw new Error('QUOTA_EXCEEDED');
        }

        throw new Error(errorData.error || 'Erreur serveur');
      }

      const medicationData = JSON.parse(rawText);

      // Validation basique
      if (!medicationData.exists) {
        console.warn('⚠️ Médicament non trouvé:', medicationName);
        return {
          exists: false,
          message: `Le médicament "${medicationName}" n'a pas été trouvé dans la base de données.`,
        };
      }

      return medicationData;
    } catch (error) {
      console.error('💥 Erreur getMedicationInfo:', error);

      // Fallback si parsing JSON échoue
      if (error instanceof SyntaxError) {
        console.error('❌ JSON invalide reçu');
        return {
          exists: false,
          error: 'Erreur de format de réponse',
          message: 'Impossible de parser les informations du médicament',
        };
      }

      throw error;
    }
  },

  /**
   * Conversation contextuelle sur un médicament
   * ✅ CORRIGÉ : Blocage automatique pour nourrissons < 1 an (utilisateurs non-medical)
   * 
   * @param {string} medicationName - Nom du médicament
   * @param {string} userQuestion - Question de l'utilisateur
   * @param {Array} conversationHistory - Historique de la conversation
   * @param {string} userProfile - Profil utilisateur
   * @param {Object} firestoreData - Données Firestore (optionnel)
   * @returns {Promise<string>} Réponse adaptée
   */
  async askAboutMedication(
    medicationName,
    userQuestion,
    conversationHistory = [],
    userProfile = 'non_medical',
    firestoreData = null
  ) {
    // ═══════════════════════════════════════════════════════════════════════
    // 🚨 DÉTECTION ÂGE DANS LA QUESTION (utilisateurs non-medical uniquement)
    // ═══════════════════════════════════════════════════════════════════════
    if (userProfile === 'non_medical') {
      const ageDetection = userQuestion.match(/(\d+)\s*(mois|ans?|semaines?|jours?)/i);
      let detectedAgeInYears = null;

      if (ageDetection) {
        const value = parseInt(ageDetection[1]);
        const unit = ageDetection[2].toLowerCase();

        if (unit.includes('jour')) {
          detectedAgeInYears = value / 365;
        } else if (unit.includes('semaine')) {
          detectedAgeInYears = value / 52;
        } else if (unit.includes('mois')) {
          detectedAgeInYears = value / 12;
        } else {
          detectedAgeInYears = value; // ans
        }
      }

      // 🚨 BLOCAGE AUTOMATIQUE SI < 1 AN
      if (detectedAgeInYears !== null && detectedAgeInYears < 1) {
        return `⚠️ **ATTENTION - SÉCURITÉ PÉDIATRIQUE**

Le médicament **${medicationName}** ne doit **JAMAIS** être administré à un nourrisson de moins de 1 an sans prescription médicale urgente délivrée par un pédiatre.

**Risques graves pour les nourrissons** :
- Détresse respiratoire
- Troubles neurologiques (convulsions, somnolence excessive)
- Risque cardiaque

**🏥 Conduite à tenir IMMÉDIATE** :
- Consultez un **pédiatre en urgence**
- Ou composez le **15** (SAMU) si symptômes inquiétants

**Aucune information posologique ne sera fournie** pour cette tranche d'âge par mesure de sécurité.`;
      }

      // ⚠️ AVERTISSEMENT RENFORCÉ SI 1-3 ANS
      if (detectedAgeInYears !== null && detectedAgeInYears >= 1 && detectedAgeInYears < 3) {
        const warningPrefix = `⚠️ **AVERTISSEMENT PÉDIATRIQUE (enfant de ${ageDetection[1]} ${ageDetection[2]})**

Pour les enfants de moins de 3 ans, **${medicationName}** doit être prescrit UNIQUEMENT par un médecin ou pédiatre.

**Risques potentiels** : troubles respiratoires, neurologiques, cardiaques.
**Ne JAMAIS administrer** sans avis médical préalable.

---

`;
        
        // On continue avec le chat, mais on ajoute ce préfixe à la réponse finale
        conversationHistory.push({
          role: 'system',
          content: warningPrefix
        });
      }
    }

    // ═══════════════════════════════════════════════════════════════════════
    // RÈGLE SÉCURITÉ PÉDIATRIQUE (pour le prompt système)
    // ═══════════════════════════════════════════════════════════════════════
    const ageSafetyRule = userProfile === 'non_medical' ? `

🚨 **RÈGLE SÉCURITÉ PÉDIATRIQUE (ABSOLUE)** :

**ENFANTS < 1 AN (NOURRISSONS)** :
- ❌ **INTERDICTION TOTALE** de suggérer une posologie
- ❌ **INTERDICTION** de valider un médicament
- ❌ **INTERDICTION** de dire "avec prudence" ou "sous surveillance médicale"
- ✅ **RÉPONSE OBLIGATOIRE** : "Ce médicament est **CONTRE-INDIQUÉ** chez les nourrissons de moins de 1 an. Consultez **immédiatement** un pédiatre ou les urgences pédiatriques."

**ENFANTS 1-3 ANS** :
- ⚠️ Mentionner systématiquement : "Médicament **STRICTEMENT sur ordonnance pédiatrique**"
- ⚠️ **NE JAMAIS** donner de posologie précise
- ⚠️ Rappeler les risques graves : détresse respiratoire, convulsions, troubles neurologiques
- ⚠️ Toujours orienter vers un médecin/pédiatre

**MÉDICAMENTS À HAUT RISQUE PÉDIATRIQUE (tous âges < 6 ans)** :
- Hydroxyzine (Atarax), benzodiazépines, opioïdes, antihistaminiques sédatifs, codéine, tramadol
- → **Refus automatique** de toute information posologique pour < 6 ans
- → Orientation **urgente** vers pédiatre

**FORMAT DE RÉPONSE POUR < 1 AN** :
"⚠️ **ATTENTION** : Ce médicament est **CONTRE-INDIQUÉ** chez les nourrissons. Risques graves : [détresse respiratoire, troubles neurologiques, convulsions]. Consultez **immédiatement** un pédiatre ou composez le **15**."

` : '';

    // ═══════════════════════════════════════════════════════════════════════
    // PROFIL GUIDANCE
    // ═══════════════════════════════════════════════════════════════════════
    const profileGuidance = {
      medecin: `PROFIL : Médecin
- Vocabulaire médical avancé
- Références scientifiques si pertinent
- Posologies précises, ajustements
- Physiopathologie si pertinent
- Monitoring et suivi thérapeutique`,

      infirmier: `PROFIL : Infirmier(e) IDE
- Vocabulaire technique professionnel
- Protocoles de surveillance
- Modalités d'administration pratiques
- Signes d'alerte et traçabilité
- Éducation thérapeutique du patient`,

      non_medical: `PROFIL : Patient / Non-médical
- VULGARISE systématiquement
- Analogies simples
- Conseils pratiques
- Signes d'alerte clairs
- Rassurant et pédagogue`,
    };

    // ═══════════════════════════════════════════════════════════════════════
    // PROMPT SYSTÈME
    // ═══════════════════════════════════════════════════════════════════════
    const systemPrompt = `Tu es un expert pharmaceutique spécialisé dans le médicament "${medicationName}".

${profileGuidance[userProfile] || profileGuidance.non_medical}

${ageSafetyRule}

CONTEXTE :
- Médicament concerné : ${medicationName}
- Tu connais déjà ce médicament en détail
- Tu peux répondre à des questions de suivi, clarifications, cas particuliers

RÈGLES DE RÉPONSE :
- Réponds UNIQUEMENT sur ce médicament
- Sois précis, factuel et sourcé
- Maximum ${userProfile === 'non_medical' ? '250' : '400'} mots par réponse
- Utilise le format Markdown (**gras** pour titres importants)
- Si question hors-sujet, redirige poliment vers le médicament
- Emojis médicaux UNIQUEMENT : 💊💉🌡️⚠️✅❌🩺🏥
- Maximum 1-2 emojis par réponse
- Adapte ton vocabulaire au profil utilisateur

CAS PARTICULIERS À GÉRER :
- Interactions spécifiques avec un autre médicament mentionné
- Posologie pour cas particulier (âge, poids, pathologie)
- Comparaison avec alternative thérapeutique
- Gestion effets indésirables
- Oubli de prise, conduite à tenir

Réponds en français de manière ${userProfile === 'medecin' ? 'scientifique et approfondie' : userProfile === 'infirmier' ? 'professionnelle et pratique' : 'accessible et rassurante'}.`;

    // ═══════════════════════════════════════════════════════════════════════
    // CONSTRUCTION DES MESSAGES
    // ═══════════════════════════════════════════════════════════════════════
    const messages = [
      { role: 'system', content: systemPrompt },
      ...conversationHistory.map((msg) => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content,
      })),
      { role: 'user', content: userQuestion },
    ];

    // ═══════════════════════════════════════════════════════════════════════
    // APPEL API
    // ═══════════════════════════════════════════════════════════════════════
    const token = await auth.currentUser.getIdToken();
    const response = await fetch(`${FUNCTIONS_URL}/askAboutMedication`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ medicationName, question: userQuestion, conversationHistory, userProfile, firestoreData }),
    });

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 429 && errorData.error === 'quota_exceeded') {
        throw new Error('QUOTA_EXCEEDED');
      }

      throw new Error(errorData.error || 'Erreur serveur');
    }

    const data = await response.json();
    return data.content;
  },

  /**
   * Recherche de médicaments par symptôme ou indication
   * @param {string} symptomOrIndication - Symptôme ou indication thérapeutique
   * @param {string} userProfile - Profil utilisateur
   * @returns {Promise<Array>} Liste de médicaments pertinents
   */
  async searchMedicationsBySymptom(symptomOrIndication, userProfile = 'non_medical') {
    const systemPrompt = `Tu es un expert en thérapeutique médicamenteuse.

Profil utilisateur : ${userProfile}

Ta mission : Suggérer les principales classes thérapeutiques et exemples de médicaments pour traiter "${symptomOrIndication}".

FORMAT DE RÉPONSE (JSON) :
{
  "indication": "${symptomOrIndication}",
  "medications": [
    {
      "classe": "Classe thérapeutique",
      "exemples": ["Médicament 1 (DCI)", "Médicament 2 (DCI)"],
      "mecanisme": "Mécanisme d'action bref (si profil médecin/infirmier)",
      "indication": "Quand prescrire en une phrase"
    }
  ],
  "avertissement": "Rappel consultation médicale obligatoire"
}

IMPORTANT :
- Maximum 3-4 classes thérapeutiques principales
- Citer DCI (+ nom commercial courant entre parenthèses)
- Adapter niveau de détail au profil
- JSON valide uniquement`;

    const messages = [
      { role: 'system', content: systemPrompt },
      {
        role: 'user',
        content: `Quels sont les traitements médicamenteux pour : ${symptomOrIndication}`,
      },
    ];

    try {
      // eslint-disable-next-line no-undef
      const content = await openaiService.call(messages, {
        maxTokens: 1500,
        temperature: 0.3,
      });

      const cleanContent = content
        .replace(/```json\n?/gi, '')
        .replace(/```\n?/g, '')
        .trim();
      return JSON.parse(cleanContent);
    } catch (error) {
      console.error('💥 Erreur searchMedicationsBySymptom:', error);
      throw error;
    }
  },
};